// Subida de binarios y del bundle (ADR 0004 §5): cola con reintentos exponenciales y progreso.
// Cada intento es una petición firmada nueva (otro nonce y otra marca de tiempo).
import { CaptureApiError, type CaptureClient } from './client.js';
import { sha256Hex } from './crypto.js';
import type { CaptureBundle, CaptureProgress, CaptureResult } from './types.js';

/** Progreso de la cola de subida. */
export interface UploadProgress {
  /** Binarios subidos / encolados. */
  done: number;
  total: number;
  /** Bytes subidos / encolados. */
  bytesDone: number;
  bytesTotal: number;
}

export interface UploaderOptions {
  /** Reintentos por petición tras el primer intento (por defecto 3). */
  retries?: number;
  /** Espera base (ms) del reintento exponencial: base, 2·base, 4·base… (por defecto 500). */
  backoffMs?: number;
  /** Subidas simultáneas (por defecto 2). */
  concurrency?: number;
  onProgress?: (p: UploadProgress) => void;
  /** Espera inyectable (pruebas). */
  sleep?: (ms: number) => Promise<void>;
}

interface Job {
  sha256: string;
  bytes: Uint8Array;
  resolve: () => void;
  reject: (err: unknown) => void;
}

/**
 * Cola de subida de la sesión.
 *
 * ```ts
 * const up = new Uploader(client, { onProgress });
 * up.enqueue(jpegBytes, sha);      // sin esperar
 * await up.flush();                // todo subido (o lanza el primer error definitivo)
 * const result = await up.complete(bundle);
 * ```
 */
export class Uploader {
  private readonly queue: Job[] = [];
  private readonly seen = new Map<string, Promise<void>>();
  private readonly errors: unknown[] = [];
  private readonly pendingEnqueues = new Set<Promise<void>>();
  private active = 0;
  private done = 0;
  private bytesDone = 0;
  private bytesTotal = 0;
  private readonly retries: number;
  private readonly backoffMs: number;
  private readonly concurrency: number;
  private readonly wait: (ms: number) => Promise<void>;

  constructor(
    private readonly client: CaptureClient,
    private readonly opts: UploaderOptions = {},
  ) {
    this.retries = opts.retries ?? 3;
    this.backoffMs = opts.backoffMs ?? 500;
    this.concurrency = Math.max(1, opts.concurrency ?? 2);
    this.wait = opts.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
  }

  /** Encola un binario (fotograma JPEG o trozo de vídeo). Un mismo hash solo se sube una vez. */
  enqueue(bytes: Uint8Array, sha256?: string): Promise<void> {
    const run = async () => {
      const hash = sha256 ?? (await sha256Hex(bytes));
      const prev = this.seen.get(hash);
      if (prev) return prev;
      const p = new Promise<void>((resolve, reject) => {
        this.queue.push({ sha256: hash, bytes, resolve, reject });
      });
      // El error queda registrado para `flush()`; aquí no se propaga como rechazo sin manejar.
      p.catch((err: unknown) => this.errors.push(err));
      this.seen.set(hash, p);
      this.bytesTotal += bytes.length;
      this.emit();
      this.pump();
      return p;
    };
    const job = run();
    this.pendingEnqueues.add(job);
    void job.catch(() => undefined).finally(() => this.pendingEnqueues.delete(job));
    return job;
  }

  /** Espera a que la cola se vacíe; lanza el primer error definitivo si lo hubo. */
  async flush(): Promise<void> {
    while (this.pendingEnqueues.size > 0) await Promise.allSettled([...this.pendingEnqueues]);
    await Promise.allSettled([...this.seen.values()]);
    if (this.errors.length > 0) throw this.errors[0];
  }

  /** Progreso actual. */
  progress(): UploadProgress {
    return {
      done: this.done,
      total: this.seen.size,
      bytesDone: this.bytesDone,
      bytesTotal: this.bytesTotal,
    };
  }

  /** `POST …/progress` (con reintentos; nunca bloquea la captura: los errores se ignoran). */
  async reportProgress(p: CaptureProgress): Promise<void> {
    try {
      await this.withRetry(() => this.client.reportProgress(p));
    } catch {
      // El progreso es informativo para el escritorio; perderlo no invalida la captura.
    }
  }

  /** Espera la cola y envía el bundle firmado a `POST …/complete`. */
  async complete(bundle: CaptureBundle): Promise<CaptureResult> {
    await this.flush();
    return this.withRetry(() => this.client.complete(bundle));
  }

  /** Ejecuta `fn` con hasta `retries` reintentos exponenciales si el error es reintentable. */
  async withRetry<T>(fn: () => Promise<T>): Promise<T> {
    for (let attempt = 0; ; attempt++) {
      try {
        return await fn();
      } catch (err) {
        const retryable = !(err instanceof CaptureApiError) || err.retryable;
        if (!retryable || attempt >= this.retries) throw err;
        await this.wait(this.backoffMs * 2 ** attempt);
      }
    }
  }

  private pump(): void {
    while (this.active < this.concurrency && this.queue.length > 0) {
      const job = this.queue.shift()!;
      this.active++;
      this.withRetry(() => this.client.uploadBinary(job.bytes, job.sha256))
        .then(() => {
          this.done++;
          this.bytesDone += job.bytes.length;
          job.resolve();
        }, job.reject)
        .finally(() => {
          this.active--;
          this.emit();
          this.pump();
        });
    }
  }

  private emit(): void {
    this.opts.onProgress?.(this.progress());
  }
}
